<template>
  <div class="mt-6">
    <div class="coupon-title">
      <h1 class="mb-2">Configuração dos cupons</h1>
      <span class="mb-4">Modelo de aplicação e tipo de desconto dos cupons.</span>
    </div>
    <mf-card-container class="mt-4" title="Desconto do cupom">
      <div class="d-flex align-center coupon-values">
        <div data-userflow-id="coupon-values-discount">
          <h2 class="mb-1">Valor do Cupom de desconto</h2>
          <p class="mb-3">Valor absoluto referente ao cupom de desconto criado. Esse valor será aplicado diretamente no cupom fiscal do consumidor.</p>
          <div class="w-300">
            <v-text-field
              v-model.lazy="campaign.value"
              v-money="money"
              :error="validations.campaign.value && validations.campaign.value.$error"
              :error-messages="errorValue"
              prefix="R$"
              outlined
              :maxlength="10"
              label="Valor de desconto"
              :disabled="disableFields"
              @keydown="isNumber"
            />
          </div>
        </div>

        <div data-userflow-id="coupon-values-minimal">
          <h2 class="mb-1">Valor mínimo de compra</h2>
          <p class="mb-3">
            Mínimo valor do cupom fiscal, para que o cupom de desconto seja aceito na compra. É necessário que esse
            <b>valor seja pelo menos o Valor de desconto + R$1.</b>
          </p>
          <div class="w-300">
            <v-text-field
              v-model.lazy="campaign.minimumValue"
              v-money="money"
              prefix="R$"
              outlined
              :maxlength="10"
              label="Valor mínimo de compra"
              :error="validations.campaign.minimumValue && validations.campaign.minimumValue.$error"
              :error-messages="errorminimumValue"
              :disabled="disableFields"
              @keydown="isNumber"
            />
          </div>
        </div>
      </div>
      <hr class="mt-1 mb-5" />
      <mf-toggle v-model="campaign.productsEnabled" :disabled="disableFields" class="mf-toggle mb-2" label="Produtos vigentes" hide-details />
      <p class="mb-0">
        Ativando essa opção, o cupom de desconto só será válido para as compras que atingirem a quantidade mínima dos produtos vigentes selecionados, ou seja
        compras sem os produtos vigentes não estarão aptas para o uso desse cupom. Com essa opção desabilitada, nenhuma regra de produto é adicionada à
        campanha.
      </p>

      <div v-if="campaign.productsEnabled">
        <div class="mt-5 mb-2 d-flex align-center">
          <h2 class="mr-1 mb-0">Quantidade mínima de produtos</h2>
          <mf-tooltip max-width="300" top variant="secondary">
            <template #activator="{ on }">
              <mf-icon dense color="cinza-l10" icon="info" />
            </template>
            Quantidade mínima dos produtos vigentes que devem estar na compra para que o cupom de desconto seja aceito.
          </mf-tooltip>
        </div>

        <div class="d-flex align-center justify-space-between">
          <div class="w-300">
            <mf-number-input
              v-model="campaign.minimumProducts"
              :disabled="disableFields"
              outlined
              label="Quantidade mínima de produtos"
              :value="campaign.minimumProducts"
              :min="0"
              show-arrows
              :error="validations.campaign.minimumProducts && validations.campaign.minimumProducts.$error"
              :error-messages="errorMinimumProducts"
            />
          </div>

          <modal-graphql-search-content
            button-activator
            hide-csv-upload
            return-groups
            button-text="Selecionar produtos"
            :selected-items.sync="campaign.products"
            :is-segmentation="false"
            :disabled="isFinished"
            @confirm="insertProducts"
          />
        </div>

        <div v-if="showUngroupedTable">
          <h2 class="mb-4">Produtos desagrupados</h2>
          <coupon-discount-products-table
            class="mt-4"
            :products="ungroupedProducts"
            :validations="validations"
            :disabled="isFinished"
            @remove-item="removeItem"
          />
        </div>

        <div v-if="showGroupedTable" class="mt-8">
          <h2 class="mb-0">Grupos de produtos</h2>

          <div v-for="group in groupedProducts" :key="group._id">
            <hr class="my-4" />
            <div class="d-flex align-center justify-space-between">
              <h2>{{ group.name }}</h2>
              <mf-button
                text
                color="vermelho-d20"
                :disabled="group.disabled || isFinished"
                label="Remover grupo"
                icon="delete"
                @click="removeGroup(group._id)"
              />
            </div>
            <coupon-discount-products-table
              class="mt-4"
              :products="group.products"
              :validations="validations"
              :disabled="isFinished"
              @remove-item="removeItemFromGroup($event, group._id)"
            />
          </div>
        </div>
      </div>
    </mf-card-container>
  </div>
</template>

<script>
import { VMoney } from 'v-money'
import { findIndex } from 'lodash'
export default {
  name: 'CouponDiscountConfigs',
  directives: { money: VMoney },
  components: {
    ModalGraphqlSearchContent: () => import('@/components/ModalGraphqlSearchContent.vue'),
    CouponDiscountProductsTable: () => import('./CouponDiscountProductsTable.vue'),
  },
  props: {
    campaign: {
      type: Object,
      default: () => {},
    },
    validations: {
      type: Object,
      default: () => {},
    },
  },
  data: () => ({
    money: {
      decimal: ',',
      thousands: '.',
      precision: 2,
      masked: false,
    },
  }),
  computed: {
    showGroupedTable() {
      return this.groupedProducts && this.groupedProducts.length > 0
    },
    showUngroupedTable() {
      return (this.ungroupedProducts.length && this.groupedProducts.length) || !this.groupedProducts.length
    },
    isFinished() {
      return this.campaign.status === 'FINISHED'
    },
    disableFields() {
      return ['FINISHED', 'ACTIVE'].includes(this.campaign.status)
    },
    ungroupedProducts() {
      if (!this.campaign.products) return []
      return this.campaign.products.filter((product) => !product.isGroup)
    },
    groupedProducts() {
      if (!this.campaign.products) return []
      return this.campaign.products.filter((product) => product.isGroup)
    },
    errorValue() {
      if (!this.validations.campaign.value) return ''
      if (this.validations.campaign.value.$error) {
        if (!this.validations.campaign.value.required) return 'Insira o valor do cupom de desconto.'
        else if (!this.validations.campaign.value.minValue) return 'O valor deve ser no mínimo R$1,00.'
      }
      return ''
    },
    errorminimumValue() {
      if (!this.validations.campaign.minimumValue) return ''
      if (this.validations.campaign.minimumValue.$error) {
        if (!this.validations.campaign.minimumValue.required) return 'Insira o valor mínimo de compra'
        else if (!this.validations.campaign.minimumValue.minValue) return 'O valor deve ser no mínimo R$1,00 a mais que o cupom de desconto.'
      }
      return ''
    },
    errorMinimumProducts() {
      if (!this.validations.campaign.minimumProducts) return ''
      if (this.validations.campaign.minimumProducts.$error) {
        if (!this.validations.campaign.minimumProducts.required) return 'Insira a quantidade mínima de produtos'
        else if (!this.validations.campaign.minimumProducts.minValue) return 'Insira um valor válido.'
      }
      return ''
    },
  },
  methods: {
    isNumber(evt) {
      const charCode = evt.which ? evt.which : evt.keyCode
      if (charCode > 31 && (charCode < 48 || charCode > 57) && charCode !== 46 && !(charCode >= 96 && charCode <= 105)) {
        evt.preventDefault()
      }
    },
    insertProducts(groupProducts) {
      if (!groupProducts) return
      let clonedProducts = []

      groupProducts.forEach((product, index) => {
        const productIndex = findIndex(this.campaign.products, (clonedProduct) => {
          return (clonedProduct._id && clonedProduct._id === product._id) || (clonedProduct.sku && clonedProduct.sku === product.sku)
        })
        if (productIndex >= 0) {
          let newProduct = {}
          if (product.products) {
            const { products, imported, ...groupWithoutProducts } = this.campaign.products[productIndex]
            newProduct = Object.assign(this.formatProduct(product, index, imported), groupWithoutProducts)
          } else {
            const isImported = false
            newProduct = Object.assign(this.formatProduct(product, index, isImported), this.campaign.products[productIndex])
          }
          clonedProducts.push(newProduct)
        } else {
          const isImported = false
          clonedProducts.push(Object.assign(this.formatProduct(product, index, isImported)))
        }
      })
      this.campaign.products = [...clonedProducts]
    },
    formatProduct(product, index, isImported) {
      let newProduct = {}
      if (product.products) {
        const formattedProducts = product.products.map((item) => {
          return {
            isGroup: true,
            imported: isImported,
            disabled: this.isDisabledProduct(isImported),
            sku: item.sku,
            ean: item.ean,
            _id: item._id,
            eans: item.eans.length === 1 ? [item.ean] : item.eans,
            name: item.name,
            origin: 'search',
            original_eans: item.product?.eans ? item.product.eans : [],
            image_url: item.image_url || item.image,
            product: item.product
              ? item.product
              : {
                  image_url: item.image,
                  ean: item.ean,
                  eans: item.eans.length > 1 ? item.eans : [item.ean],
                },
            index,
          }
        })
        newProduct = Object.assign(
          {
            _id: product._id,
            isGroup: true,
            imported: isImported,
            disabled: this.isDisabledProduct(isImported),
            name: product.name,
            products: formattedProducts,
            group_image_url: product.image_url,
            categories_final_consumer: product.categories_final_consumer,
            index,
          },
          this.productsExtraFieldsInfo
        )
      } else {
        newProduct = Object.assign(
          {
            _id: product._id,
            imported: isImported,
            disabled: this.isDisabledProduct(isImported),
            active: typeof product.active === 'boolean' ? product.active : true,
            sku: product.sku,
            ean: product.ean,
            eans: product?.eans?.length === 1 ? [product.ean] : product.eans,
            name: product.name,
            image_url: product.image_url || product.image,
            product: {
              image: product.image,
              ean: product.ean,
              eans: product.eans.length > 1 ? product.eans : [product.ean],
            },
            index,
          },
          this.productsExtraFieldsInfo
        )
      }
      return newProduct
    },
    isDisabledProduct(isImported) {
      return (
        this.campaign.status === 'finished' ||
        this.campaign.status === 'expired' ||
        this.campaign.status === 'industry_rejected' ||
        this.campaign.status === 'processing' ||
        ((this.campaign.status === 'active' || this.campaign.status === 'scheduled') && !isImported)
      )
    },
    removeItem(item) {
      const newProducts = this.campaign.products
        .map((offer) => {
          if (offer.sku !== item.sku) return offer
        })
        .filter((item) => item)
      this.campaign.products = newProducts
    },
    removeItemFromGroup(item, groupId) {
      const newProducts = this.campaign.products.map((offer) => {
        if (offer._id === groupId) {
          return {
            ...offer,
            products: offer.products
              .map((product) => {
                if (product.sku !== item.sku) return product
              })
              .filter((item) => item),
          }
        }
        return offer
      })
      this.campaign.products = newProducts
    },
    removeGroup(groupId) {
      const newProducts = this.campaign.products
        .map((offer) => {
          if (offer._id !== groupId) return offer
        })
        .filter((item) => item)
      this.campaign.products = newProducts
    },
  },
}
</script>

<style lang="scss" scoped>
hr {
  border: 1px solid $cinza-l60;
}

p {
  font-size: 14px;
  color: $cinza-0;
}

h2 {
  font-size: 16px;
  font-weight: 600;
  color: $cinza-d10;
}
.coupon-title {
  h1 {
    font-size: 22px;
    font-weight: 700;
    color: $cinza-d10;
  }

  span {
    font-size: 16px;
    color: $cinza-0;
  }
}

.coupon-values {
  gap: 64px;
  > div {
    max-width: 488px;
  }
}

.mf-toggle {
  ::v-deep label {
    color: $cinza-d10 !important;
    font-weight: 600 !important;
    font-size: 16px !important;
  }
}

.w-300 {
  width: 300px;
}
</style>
